import { t } from "@lingui/macro";
import {
  type InvoiceBudgetItemListItemFragment,
  type OiBudgetItemsTimeBillingOrderByColumn,
  OiBudgetItemsTimeBillingWhereColumn,
  OutgoingInvoiceItemsListingBudgetItemFilterOptionsDocument,
  type OutgoingInvoiceItemsListingBudgetItemFilterOptionsQuery,
  type OutgoingInvoiceItemsListingBudgetItemFilterOptionsQueryVariables,
  OutgoingInvoiceItemsListingDocument,
  OutgoingInvoiceItemsListingFilterOptionsDocument,
  type OutgoingInvoiceItemsListingFilterOptionsQuery,
  type OutgoingInvoiceItemsListingFilterOptionsQueryVariables,
  type OutgoingInvoiceItemsListingQuery,
  type OutgoingInvoiceItemsListingQueryVariables,
  OutgoingInvoiceItemsListingSummaryDocument,
  type OutgoingInvoiceItemsListingSummaryQuery,
  type OutgoingInvoiceItemsListingSummaryQueryVariables,
  OutgoingInvoiceTypeEnum,
  type SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import { QuickFilters } from "@src/components/modules/invoices/outgoing/listing/StoreOutgoingInvoices";
import type { BodyRow } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { appStore } from "@src/stores/AppStore";
import { commonSearchParams } from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getBudgetItemTypeFilterOptions,
  getPaymentStatusFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import {
  createActiveQueryParam,
  getQueryParamOptional,
} from "@src/utils/routing";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import type { Query } from "nextjs-routes";

export interface OutgoingInvoiceItemsTableItem
  extends BodyRow,
    InvoiceBudgetItemListItemFragment {}

export class StoreOutgoingInvoiceItems {
  readonly tableKey = "outgoing-invoice-items-listing";

  pagination = new PaginationState(this.tableKey, {
    onChangePagination: () => {
      this.fetchItems();
    },
  });

  orderBy = new OrderBy<OiBudgetItemsTimeBillingOrderByColumn>();

  where = new Filters<OiBudgetItemsTimeBillingWhereColumn>(
    [
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
        onChange: (value) => this.handleProjectFilterChange(value),
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.ProjectCategory,
        operator: SqlOperator.In,
        title: t`Project category`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.BudgetItemId,
        operator: SqlOperator.In,
        noOptionsMessage: t`Please select Project first`,
        title: t`Budget item`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.BudgetItemType,
        operator: SqlOperator.In,
        title: t`Budget item type`,
        options: getBudgetItemTypeFilterOptions(),
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.OwnerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.DateOfSupply,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Date of supply`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.DueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Due date`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Issue date`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        dateRange: true,
        title: t`Paid at`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.Type,
        operator: SqlOperator.In,
        title: t`Type`,
        options: [
          {
            label: t`Invoice`,
            value: OutgoingInvoiceTypeEnum.Invoice,
          },
          {
            label: t`Payment receipt`,
            value: OutgoingInvoiceTypeEnum.ProformaPayment,
          },
          {
            label: t`Credit note`,
            value: OutgoingInvoiceTypeEnum.CreditNote,
          },
        ],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: OiBudgetItemsTimeBillingWhereColumn.WorkspaceBankAccountId,
        operator: SqlOperator.In,
        title: t`Agency bank account`,
        options: [],
      }),
      new Filter({
        hidden: !appStore.workspaceStore.hasModuleForPage("time_billing"),
        column: OiBudgetItemsTimeBillingWhereColumn.HasTimeBilling,
        title: t`Has time billing`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
        value: [],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
      },
      tableKey: this.tableKey,
      orderBy: this.orderBy,
    },
  );

  @observable.ref
  tableData: OutgoingInvoiceItemsTableItem[] = [];

  @observable.ref
  searchTerm = "";

  @observable.ref
  summary:
    | OutgoingInvoiceItemsListingSummaryQuery["oiBudgetItemsForTimeBillingSummary"]
    | null = null;

  private itemsFetcher = new FetchHelper<
    OutgoingInvoiceItemsListingQuery,
    OutgoingInvoiceItemsListingQueryVariables
  >(OutgoingInvoiceItemsListingDocument);

  private filterOptionsFetcher = new FetchHelper<
    OutgoingInvoiceItemsListingFilterOptionsQuery,
    OutgoingInvoiceItemsListingFilterOptionsQueryVariables
  >(OutgoingInvoiceItemsListingFilterOptionsDocument);

  private budgetItemFilterOptionsFetcher = new FetchHelper<
    OutgoingInvoiceItemsListingBudgetItemFilterOptionsQuery,
    OutgoingInvoiceItemsListingBudgetItemFilterOptionsQueryVariables
  >(OutgoingInvoiceItemsListingBudgetItemFilterOptionsDocument);

  private summaryFetcher = new FetchHelper<
    OutgoingInvoiceItemsListingSummaryQuery,
    OutgoingInvoiceItemsListingSummaryQueryVariables
  >(OutgoingInvoiceItemsListingSummaryDocument);

  constructor() {
    makeObservable(this);
    this.setFiltersFromQuery(Router.query);
    this.fetchFilterOptions();
    this.fetchSummary();
    this.fetchItems();
  }

  @computed
  get isLoading(): boolean {
    return this.itemsFetcher.isLoading.value;
  }

  @computed
  get isSummaryLoading(): boolean {
    return this.summaryFetcher.isLoading.value;
  }

  @computed
  get searchParams() {
    return {
      ...commonSearchParams(this),
      ...createActiveQueryParam(QuickFilters.InvoiceItems),
    };
  }

  @action.bound
  setSearchTerm(value: string): void {
    if (this.searchTerm === value) return;
    this.searchTerm = value;
    this.fetchSummary();
    this.pagination.resetPage();
  }

  @action.bound
  handleOrderChange(orderBy: {
    column: OiBudgetItemsTimeBillingOrderByColumn;
    order: SortOrder | undefined;
  }): void {
    if (!orderBy.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }

    this.orderBy.setClauses([{ column: orderBy.column, order: orderBy.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  private async fetchFilterOptions(): Promise<void> {
    const [data, error] = await this.filterOptionsFetcher.fetch();

    if (error || !data) return;

    this.where.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.OwnerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );

    this.where.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));

    this.where.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.ProjectCategory)
      ?.setOptions(mapToOptions.toOptions(data.projectCategories, "name"));

    this.where.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));

    const workspaceCompaniesFilter = this.where.filtersByColumn.get(
      OiBudgetItemsTimeBillingWhereColumn.WorkspaceCompanyId,
    );

    workspaceCompaniesFilter?.setOptions(
      mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
    );

    if (appStore.workspaceStore.companies.length <= 1) {
      workspaceCompaniesFilter?.hide();
    }

    this.where.filtersByColumn
      .get(OiBudgetItemsTimeBillingWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(
        mapToOptions.companiesToBankAccountOptions(
          appStore.workspaceStore.companies,
        ),
      );
  }

  @action.bound
  private async fetchItems(): Promise<void> {
    const [data, error] = await this.itemsFetcher.fetch({
      filters: {
        first: this.pagination.asParams.first,
        page: this.pagination.asParams.page,
        where: this.where.asGraphQueryParam,
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
      },
    });

    if (error || !data.oiBudgetItemsForTimeBilling) return;

    this.tableData = data.oiBudgetItemsForTimeBilling.data.map((item) => ({
      ...item,
      id: `${item.outgoing_invoice_id}-${item.budget_item_id}-${item.budget_item_type}`,
    }));

    this.pagination.setFromPaginatorInfo(
      data.oiBudgetItemsForTimeBilling.paginatorInfo,
    );
  }

  @action.bound
  private async fetchSummary(): Promise<void> {
    const [data, error] = await this.summaryFetcher.fetch({
      filters: {
        where: this.where.asGraphQueryParam,
        search: this.searchTerm,
      },
    });

    if (error || !data?.oiBudgetItemsForTimeBillingSummary) return;

    this.summary = data.oiBudgetItemsForTimeBillingSummary;
  }

  @action.bound
  private setFiltersFromQuery(query: Query): void {
    this.searchTerm = getQueryParamOptional("search", query) ?? "";
    this.where.fromURLSearchParam(query);
    this.orderBy.fromURLSearchParam(query);
    this.pagination.fromURLSearchParam(query);
  }

  @action.bound
  private async handleProjectFilterChange(
    value: string[] | undefined,
  ): Promise<void> {
    const budgetItemFilter = this.where.filtersByColumn.get(
      OiBudgetItemsTimeBillingWhereColumn.BudgetItemId,
    );

    if (!value || value?.length === 0) {
      budgetItemFilter?.clear();
      budgetItemFilter?.setOptions([]);
      budgetItemFilter?.setNoOptionsMessage(t`Please select Project first`);
      return;
    }

    budgetItemFilter?.setNoOptionsMessage(t`No options`);
    const [data, error] = await this.budgetItemFilterOptionsFetcher.fetch({
      projectId: value[0],
    });

    if (error || !data) {
      budgetItemFilter?.setOptions([]);
      return;
    }

    budgetItemFilter?.setOptions(
      mapToOptions.budgetItems(
        data.ourWorkBudgetItemsSimpleMap.concat(
          // @ts-expect-error - Typename mismatch but otherwise they are the same
          data.expenseBudgetItemsSimpleMap,
        ),
      ),
    );
  }
}
